/**
 * Irrigation system definitions (efficiency + application rate) used by the watering
 * calculator. The per-species watering-needs data that used to live here
 * (WATERING_REQUIREMENTS) was retired in favor of a single source of truth:
 * GRASS_CHARACTERISTICS.wateringNeeds in ./grass-characteristics. The filename is
 * kept to avoid churn on IRRIGATION_SYSTEMS importers.
 */

export interface IrrigationSystem {
  name: string
  efficiency: number  // percentage (0.7 = 70% efficiency)
  applicationRate: number  // inches per hour
  description: string
  bestFor: string[]
}

export const IRRIGATION_SYSTEMS: Record<string, IrrigationSystem> = {
  'Sprinkler System': {
    name: 'Sprinkler System',
    efficiency: 0.75,
    applicationRate: 0.5,
    description: 'Automatic underground sprinkler system with rotor or mixed heads',
    bestFor: ['Large lawns', 'Convenience', 'Even coverage']
  },
  'Spray Heads': {
    name: 'Spray Heads',
    efficiency: 0.75,
    applicationRate: 1.75,
    description: 'In-ground pop-up spray heads with a faster precipitation rate',
    bestFor: ['Small zones', 'Tight areas', 'Shorter run times']
  },
  'Rotor Heads': {
    name: 'Rotor Heads',
    efficiency: 0.75,
    applicationRate: 0.5,
    description: 'In-ground rotor or rotary nozzle zones with slower output',
    bestFor: ['Large zones', 'Wind tolerance', 'Longer throw']
  },
  'Oscillating Sprinkler': {
    name: 'Oscillating Sprinkler',
    efficiency: 0.65,
    applicationRate: 0.3,
    description: 'Portable oscillating sprinkler for rectangular areas',
    bestFor: ['Medium lawns', 'Portability', 'Cost-effective']
  },
  'Rotary Sprinkler': {
    name: 'Rotary Sprinkler',
    efficiency: 0.70,
    applicationRate: 0.4,
    description: 'Circular coverage rotary sprinkler',
    bestFor: ['Circular areas', 'Good coverage', 'Adjustable']
  },
  'Soaker Hose': {
    name: 'Soaker Hose',
    efficiency: 0.90,
    applicationRate: 0.1,
    description: 'Porous hose that seeps water directly to soil',
    bestFor: ['Water conservation', 'Garden beds', 'Root watering']
  },
  'Hose and Sprayer': {
    name: 'Hose and Sprayer',
    efficiency: 0.60,
    applicationRate: 0.8,
    description: 'Manual watering with hose and spray nozzle',
    bestFor: ['Small areas', 'Precise control', 'Manual operation']
  },
  'Drip Irrigation': {
    name: 'Drip Irrigation',
    efficiency: 0.95,
    applicationRate: 0.05,
    description: 'Slow, precise water delivery to plant roots',
    bestFor: ['Water conservation', 'Precise watering', 'Efficient']
  }
}
